/*
 * /#/webmcp-debug — the WebMCP diagnostics panel and development MCP simulator.
 *
 * Two jobs:
 *  1. Diagnose. Is WebMCP available, through which transport, which tools did the
 *     browser actually accept, and what happened on the last invocation.
 *  2. Simulate. Invoke any registered tool with arbitrary JSON arguments, through
 *     the *same* ToolRegistry an agent reaches — no duplicated logic, so a green
 *     result here means the agent path is green too.
 *
 * Shows nothing secret: everything here is derived from the page URL, the browser,
 * and the tool registry.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toolRegistry, webMcpAdapter } from '../webmcp/index.js'

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }
const card = {
  border: '1px solid #d3d3d3',
  borderRadius: 6,
  padding: '12px 16px',
  marginBottom: 16,
  background: '#fff'
}
const pre = { ...mono, background: '#f5f5f5', padding: 10, borderRadius: 4, overflowX: 'auto', margin: 0 }

function Badge ({ tone, children }) {
  const colors = {
    ok: { bg: '#e3f3e5', fg: '#166534' },
    warn: { bg: '#fdf3d8', fg: '#854d0e' },
    bad: { bg: '#fde2e2', fg: '#991b1b' }
  }[tone] || { bg: '#eee', fg: '#333' }
  return (
    <span style={{
      background: colors.bg,
      color: colors.fg,
      borderRadius: 4,
      padding: '2px 8px',
      fontWeight: 600,
      fontSize: 12,
      whiteSpace: 'nowrap'
    }}>{children}</span>
  )
}

function Row ({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '3px 0' }}>
      <div style={{ width: 190, color: '#666', flexShrink: 0 }}>{label}</div>
      <div style={{ ...mono, wordBreak: 'break-all' }}>{children}</div>
    </div>
  )
}

export const WebMcpDebug = () => {
  const [status, setStatus] = useState(() => webMcpAdapter.status())
  const [tools, setTools] = useState(() => toolRegistry.list())
  const [invocations, setInvocations] = useState(() => toolRegistry.invocations())
  const [browserTools, setBrowserTools] = useState(null)
  const [browserToolsError, setBrowserToolsError] = useState(null)

  const [selected, setSelected] = useState('')
  const [argsText, setArgsText] = useState('{}')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const offRegistry = toolRegistry.subscribe(() => {
      setTools(toolRegistry.list())
      setInvocations(toolRegistry.invocations())
    })
    const offAdapter = webMcpAdapter.subscribe(setStatus)
    return () => { offRegistry(); offAdapter() }
  }, [])

  useEffect(() => {
    if (!selected && tools.length) setSelected(tools[0].name)
  }, [tools, selected])

  const refreshBrowserTools = useCallback(async () => {
    setBrowserToolsError(null)
    try {
      setBrowserTools(await webMcpAdapter.browserTools())
    } catch (err) {
      setBrowserTools(null)
      setBrowserToolsError(err.message)
    }
  }, [])

  useEffect(() => { refreshBrowserTools() }, [refreshBrowserTools, status.transport])

  const selectedTool = useMemo(() => tools.find((t) => t.name === selected), [tools, selected])

  const invoke = useCallback(async (via) => {
    setBusy(true)
    setResult(null)
    let args
    try {
      args = argsText.trim() === '' ? {} : JSON.parse(argsText)
    } catch (err) {
      setResult({ ok: false, error: { code: 'INVALID_JSON', message: `Arguments are not valid JSON: ${err.message}` } })
      setBusy(false)
      return
    }
    try {
      const res = via === 'webmcp'
        ? await webMcpAdapter.executeViaBrowser(selected, args)
        : await toolRegistry.invoke(selected, args, { source: 'debug-panel' })
      setResult(res)
    } catch (err) {
      setResult({ ok: false, error: { code: 'TRANSPORT_ERROR', message: err.message } })
    } finally {
      setBusy(false)
    }
  }, [selected, argsText])

  const transportTone = status.transport === 'native' ? 'ok' : status.transport === 'polyfill' ? 'warn' : 'bad'
  const last = invocations[0]
  const env = status.environment

  return (
    <div style={{ maxWidth: 960, padding: '24px 32px 48px' }} data-testid='webmcp-debug'>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
        <h1 style={{ marginTop: 0, marginBottom: 16 }}>WebMCP diagnostics</h1>
        {/* The only navigation in the app. Without it this deep link is a dead end. */}
        <a href='#/editor' style={{ fontSize: 14 }}>Open the editor</a>
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Support</h2>
        <Row label='WebMCP supported'>
          <Badge
            tone={status.connecting ? 'warn' : status.available ? 'ok' : 'bad'}
            data-testid='webmcp-supported'
          >
            {status.connecting ? 'connecting…' : status.available ? 'yes' : 'no'}
          </Badge>
        </Row>
        <Row label='Transport'>
          <Badge tone={status.connecting ? 'warn' : transportTone}>
            {status.connecting ? 'connecting…' : status.transport}
          </Badge>
          {status.transport === 'polyfill' && (
            <span style={{ color: '#666', marginLeft: 8, fontFamily: 'inherit' }}>
              polyfilled by @mcp-b/webmcp-polyfill — callable from this page and from tests,
              but no AI client is attached
            </span>
          )}
        </Row>
        <Row label='Native WebMCP in browser'>{String(status.nativeAvailable)}</Row>
        <Row label='Accessor'>
          {status.accessor ? `${status.accessor}.modelContext` : '—'}
          {status.accessor === 'navigator' && (
            <span style={{ color: '#854d0e', marginLeft: 8, fontFamily: 'inherit' }}>
              legacy accessor (Chrome 149/150)
            </span>
          )}
        </Row>
        <Row label='Registered with browser'>{status.registeredToolNames.join(', ') || '(none)'}</Row>
        {status.error && <Row label='Error'>{status.error.name}: {status.error.message}</Row>}
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Environment</h2>
        <Row label='Hostname'>{env.hostname}</Row>
        <Row label='Deployment'>{env.environment}{env.workspace ? ` (${env.workspace})` : ''}</Row>
        <Row label='Runtime namespace'>{env.runtimeNamespace || '—'}</Row>
        <Row label='Secure context'>
          <Badge tone={env.secureContext ? 'ok' : 'bad'}>{String(env.secureContext)}</Badge>
        </Row>
        <Row label='Top-level document'>
          <Badge tone={env.topLevel ? 'ok' : 'warn'}>{String(env.topLevel)}</Badge>
          {!env.topLevel && (
            <span style={{ color: '#854d0e', marginLeft: 8, fontFamily: 'inherit' }}>
              tools registered in an iframe are not discovered by ChatGPT Desktop
            </span>
          )}
        </Row>
        <Row label='Origin-agent-cluster'>{String(env.originAgentCluster)}</Row>
        <Row label='User agent'>{status.userAgent}</Row>
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Registered tools ({tools.length})</h2>
        {tools.map((t) => (
          <details key={t.name} style={{ marginBottom: 8 }}>
            <summary style={{ cursor: 'pointer' }}>
              <span style={{ ...mono, fontWeight: 600 }}>{t.name}</span>
              {t.readOnly ? <span style={{ marginLeft: 8 }}><Badge tone='ok'>read-only</Badge></span> : null}
              <span style={{ color: '#666', marginLeft: 8 }}>{t.title}</span>
            </summary>
            <p style={{ margin: '6px 0' }}>{t.description}</p>
            <pre style={pre}>{JSON.stringify(t.inputSchema, null, 2)}</pre>
          </details>
        ))}
        {tools.length === 0 && <p>No tools registered.</p>}
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>
          As the browser sees them{' '}
          <button onClick={refreshBrowserTools} style={{ fontSize: 12, marginLeft: 8 }}>refresh</button>
        </h2>
        <p style={{ color: '#666', marginTop: 0 }}>
          Read back through <code style={mono}>document.modelContext.getTools()</code> — this is the
          agent&apos;s view, so a tool missing here would be invisible to ChatGPT.
        </p>
        {browserToolsError && <pre style={pre}>{browserToolsError}</pre>}
        {browserTools && <pre style={pre} data-testid='browser-tools'>{JSON.stringify(browserTools, null, 2)}</pre>}
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Tool simulator</h2>
        <p style={{ color: '#666', marginTop: 0 }}>
          Calls the same <code style={mono}>ToolRegistry.invoke()</code> an agent reaches.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <label style={{ display: 'block' }}>
            <div style={{ color: '#666', fontSize: 12 }}>Tool</div>
            <select
              data-testid='tool-select'
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setResult(null) }}
              style={{ ...mono, padding: 6, minWidth: 260 }}
            >
              {tools.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
          </label>
          <label style={{ flex: 1, minWidth: 280 }}>
            <div style={{ color: '#666', fontSize: 12 }}>Arguments (JSON)</div>
            <textarea
              data-testid='tool-args'
              value={argsText}
              onChange={(e) => setArgsText(e.target.value)}
              rows={5}
              style={{ ...mono, width: '100%', padding: 6 }}
            />
          </label>
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <button data-testid='invoke-registry' disabled={busy || !selectedTool} onClick={() => invoke('registry')}>
            Invoke via registry
          </button>
          <button data-testid='invoke-webmcp' disabled={busy || !status.available} onClick={() => invoke('webmcp')}>
            Invoke via document.modelContext
          </button>
        </div>
        {result !== null && (
          <pre style={{ ...pre, marginTop: 12 }} data-testid='invoke-result'>{JSON.stringify(result, null, 2)}</pre>
        )}
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>
          Last invocation{' '}
          <button onClick={() => { toolRegistry.clearInvocations() }} style={{ fontSize: 12, marginLeft: 8 }}>clear</button>
        </h2>
        {!last && <p style={{ margin: 0 }}>Nothing invoked yet.</p>}
        {last && (
          <>
            <Row label='Tool'>{last.tool}</Row>
            <Row label='Source'>{last.source}</Row>
            <Row label='Timestamp'>{last.timestamp}</Row>
            <Row label='Duration'>{last.durationMs} ms</Row>
            <Row label='Outcome'>
              <Badge tone={last.ok ? 'ok' : 'bad'}>{last.ok ? 'success' : 'failure'}</Badge>
            </Row>
            <Row label='Arguments'>&nbsp;</Row>
            <pre style={pre}>{JSON.stringify(last.args, null, 2)}</pre>
            <Row label={last.ok ? 'Result' : 'Error'}>&nbsp;</Row>
            <pre style={pre} data-testid='last-invocation-payload'>
              {JSON.stringify(last.ok ? last.result : last.error, null, 2)}
            </pre>
          </>
        )}
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Invocation history ({invocations.length})</h2>
        <table style={{ ...mono, borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#666' }}>
              <th style={{ padding: 4 }}>#</th>
              <th style={{ padding: 4 }}>time</th>
              <th style={{ padding: 4 }}>tool</th>
              <th style={{ padding: 4 }}>source</th>
              <th style={{ padding: 4 }}>ms</th>
              <th style={{ padding: 4 }}>outcome</th>
            </tr>
          </thead>
          <tbody>
            {invocations.map((i) => (
              <tr key={i.id} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: 4 }}>{i.id}</td>
                <td style={{ padding: 4 }}>{i.timestamp.slice(11, 23)}</td>
                <td style={{ padding: 4 }}>{i.tool}</td>
                <td style={{ padding: 4 }}>{i.source}</td>
                <td style={{ padding: 4 }}>{i.durationMs}</td>
                <td style={{ padding: 4 }}>{i.ok ? 'ok' : (i.error && i.error.code) || 'error'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default WebMcpDebug
